import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SettlementView from "@/components/SettlementView";
import PrintButtons from "@/components/PrintButtons";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

function DisburseForm({ s, onDone }) {
  const [momo, setMomo] = useState(Object.fromEntries(s.lines.map((l) => [l.role, l.momo || ""])));
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/settlements/${s.id}/disburse`, { momo }); toast.success("Money disbursed to all beneficiaries"); onDone(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border-2 border-blue-100 bg-blue-50/50 p-4" data-testid="disburse-form">
      <div className="font-display font-bold">Disburse from central MoMo account</div>
      <p className="text-xs font-semibold text-amber-700">Demo mode: MoMo payouts are SIMULATED - no real transfer happens yet.</p>
      {s.lines.map((l) => (
        <div key={l.role} className="grid grid-cols-[1fr_auto] items-center gap-3 sm:grid-cols-[160px_1fr_auto]">
          <span className="text-sm font-semibold">{l.label}</span>
          <Input value={momo[l.role]} onChange={(e) => setMomo({ ...momo, [l.role]: e.target.value })} placeholder="MoMo number" className="col-span-2 bg-white sm:col-span-1" data-testid={`disburse-momo-${l.role}`} />
          <b className="text-right text-sm">{money(l.amount)}</b>
        </div>
      ))}
      <Button disabled={busy} onClick={go} className="h-11 w-full rounded-xl bg-emerald-600 font-bold hover:bg-emerald-700" data-testid="disburse-confirm-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" />Disburse {money(s.lines.reduce((a, l) => a + l.amount, 0))}</>}
      </Button>
    </div>
  );
}

// Slip + print + disbursement; used from the sign-out flow and the Sign-out Bus tab
export function SettlementPanel({ s, onChanged, readOnly = false }) {
  const { user } = useAuth();
  const [cur, setCur] = useState(s);
  useEffect(() => setCur(s), [s]);
  return (
    <div className="space-y-3" data-testid="settlement-panel">
      <PrintButtons testPrefix="settlement-print">{(f) => <SettlementView s={cur} format={f} />}</PrintButtons>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 p-4"><SettlementView s={cur} /></div>
      {!readOnly && cur.status === "pending" && user?.role === "admin" && <DisburseForm s={cur} onDone={(d) => { setCur(d); onChanged?.(); }} />}
    </div>
  );
}
